import { of } from 'rxjs';
import {
  CAROUSEL_LAYOUT_CONFIG,
  CAROUSEL_LAYOUT_DEFAULT_CONFIG,
  HEADER_MAR_CONFIG_TOKEN,
  INVOICE_SUMMARY_CONFIG,
  HeaderMARConfig,
  InvoiceListResponse,
  MultipleAccountsRelationship,
  MultipleAccountsRelationshipServiceIdentity,
} from '@celerofinancas/pcp-client-ui';
import { MONEY_PIPE_TOKEN } from '@celerofinancas/ui-pipes';
import { CreditCardIdentityService } from '@pages/credit-cards/credit-card-identity.service';

/**
 * Tokens for CreditCardSummaryComponent and its dependencies.
 */
export const CREDIT_CARD_SUMMARY_TOKENS = [
  {
    provide: HEADER_MAR_CONFIG_TOKEN,
    useFactory: (
      identity: MultipleAccountsRelationshipServiceIdentity,
      creditCardIdentity: CreditCardIdentityService,
    ): HeaderMARConfig => ({
      title:
        creditCardIdentity.get()?.name ??
        creditCardIdentity.get()?.contractedProduct ??
        'Cartão de Crédito',
      columns: [
        {
          key: 'issuer_bank_number',
          label: 'Banco',
          render: (item: unknown) =>
            (item as MultipleAccountsRelationship)?.issuer_bank_number ?? '-',
        },
        {
          key: 'card_brand',
          label: 'Bandeira',
          render: (item: unknown) => (item as MultipleAccountsRelationship)?.card_brand ?? '-',
        },
        // The "Vencimento da fatura" column is intentionally absent: the gRPC contract
        // (CreditCardAccount) has no due_day field, so /v2/credit-cards/accounts/list never
        // returns it and the column always rendered '-'. Card 86e37uygf: keep it hidden until
        // the account contract exposes the due day. The bill due date remains visible in the
        // invoice summary below, sourced from CreditCardBill.due_date.
        {
          key: 'product_tier',
          label: 'Apelido do produto',
          editable: true,
          render: (item: unknown) => (item as MultipleAccountsRelationship)?.product_tier ?? '-',
        },
      ],
      fetchData: () => of(identity.get()),
    }),
    deps: [MultipleAccountsRelationshipServiceIdentity, CreditCardIdentityService],
  },
  {
    provide: MONEY_PIPE_TOKEN,
    useValue: {
      prefix: 'R$',
      thousand: '.',
      decimalSeparator: ',',
      showNegative: false,
      displayThousands: true,
      showIndicator: false,
    },
  },
  {
    provide: CAROUSEL_LAYOUT_CONFIG,
    useValue: {
      ...CAROUSEL_LAYOUT_DEFAULT_CONFIG,
      itemWidth: 151,
      gaps: { small: 24, medium: 24, large: 24 },
    },
  },
  {
    provide: INVOICE_SUMMARY_CONFIG,
    useValue: {
      // Caixa não exibe o botão "Ver mais detalhes" no resumo da fatura
      // (não previsto no protótipo). Core exibe por default.
      showInvoiceDetailsButton: false,
      groups: [[
        { type: 'status' },
        {
          type: 'text',
          label: 'Mês e Ano',
          getValue: (inv: InvoiceListResponse) => {
            const source = inv.due_at ?? (inv.year_month ? `${inv.year_month}-01` : null);
            if (!source) return '-';
            const [year, month] = source.split('-');
            const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
                            'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
            return `${months[+month - 1]} ${year}`;
          },
        },
        { type: 'money', label: 'Valor total (R$)', getValue: (inv: InvoiceListResponse) => inv.value },
        { type: 'money', label: 'Pagamento mínimo (R$)', getValue: (inv: InvoiceListResponse) => inv.minimum_payment },
        { type: 'date', label: 'Vencimento', getValue: (inv: InvoiceListResponse) => inv.due_at },
      ]],
    },
  },
];
